import React, {
  cloneElement,
  isValidElement,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
// @ts-expect-error - this will be resolved at build time
import { Box, Text, Icon } from "@xsolla/xui-primitives";
import { useResolvedTheme } from "@xsolla/xui-core";
import {
  CheckCr,
  ExclamationMarkCr,
  InfoCr,
  Remove,
  Warning as WarningIcon,
} from "@xsolla/xui-icons-base";
import type { ToastColorScheme, ToastProps, ToastType } from "./types";

const TOAST_COLORS: Record<ToastType, string> = {
  alert: "#ff553d",
  success: "#47d94a",
  neutral: "#7899a1",
  warning: "#ffb95c",
};
const DEFAULT_COLOR_SCHEME: ToastColorScheme = {
  background: "#34474b",
  text: "#ffffff",
  descriptionText: "rgba(255, 255, 255, 0.7)",
  divider: "rgba(255, 255, 255, 0.16)",
  actionText: "#ffffff",
};

const getDefaultIcon = (
  type: ToastType,
  size: number,
  color: string
): React.ReactNode => {
  switch (type) {
    case "success":
      return (
        <CheckCr
          variant="solid"
          size={size}
          color={color}
          data-testid="toast-icon-success"
        />
      );
    case "neutral":
      return (
        <InfoCr
          variant="solid"
          size={size}
          color={color}
          data-testid="toast-icon-neutral"
        />
      );
    case "warning":
      return (
        <WarningIcon
          variant="solid"
          size={size}
          color={color}
          data-testid="toast-icon-warning"
        />
      );
    case "alert":
    default:
      return (
        <ExclamationMarkCr
          variant="solid"
          size={size}
          color={color}
          data-testid="toast-icon-alert"
        />
      );
  }
};

const ANIMATION_DURATION = 200;
const PROGRESS_HEIGHT = 4;
const TOAST_CONFIG = {
  minHeight: 56,
  paddingHorizontal: 16,
  paddingVertical: 12,
  gap: 12,
  iconSize: 24,
  closeButtonSize: 24,
  closeIconSize: 20,
  fontSize: 16,
  lineHeight: 20,
  maxWidth: 440,
};

type ActionProps = {
  children?: React.ReactNode;
  size?: "xs";
  variant?: "inverse";
  background?: boolean;
  themeMode?: ToastProps["themeMode"];
  themeProductContext?: ToastProps["themeProductContext"];
};

export const Toast: React.FC<ToastProps> = ({
  id,
  type = "neutral",
  title,
  description,
  // No clamp by default: the toast grows to fit the description. A hardcoded
  // line limit silently truncated real error copy at narrow widths (XUITSD-20),
  // and translations only make the strings longer. Consumers that genuinely
  // need a cap opt in via `descriptionLines`.
  descriptionLines,
  icon = true,
  action,
  showCloseButton = true,
  progress = false,
  duration,
  onClose,
  colorScheme = DEFAULT_COLOR_SCHEME,
  testID,
  themeMode,
  themeProductContext,
}) => {
  const { theme } = useResolvedTheme({ themeMode, themeProductContext });
  const config = TOAST_CONFIG;
  const [visible, setVisible] = useState(false);
  const [dismissing, setDismissing] = useState(false);
  const [progressComplete, setProgressComplete] = useState(false);

  // Hold the latest `onClose` in a ref so `handleClose` can stay referentially
  // stable. `ToastGroup` renders every toast with
  // `onClose={() => onDismiss(toast.id)}` — a brand new closure on each render.
  // Depending on it directly would re-run the auto-dismiss effect (and restart
  // the countdown) every time the toast list changed, so a newly added toast
  // would reset the timers of the ones already on screen. See FEP-861.
  const onCloseRef = useRef(onClose);
  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  // Re-entry guard for `handleClose`. A ref rather than the `dismissing` state
  // so the callback does not need `dismissing` in its dependency list.
  const dismissingRef = useRef(false);

  useEffect(() => {
    const frame = requestAnimationFrame(() => setVisible(true));
    return () => cancelAnimationFrame(frame);
  }, []);

  const handleClose = useCallback(() => {
    if (dismissingRef.current) return;
    dismissingRef.current = true;
    setDismissing(true);
    setTimeout(() => onCloseRef.current?.(), ANIMATION_DURATION);
  }, []);

  // Deliberately depends only on `duration` (and the now-stable `handleClose`):
  // each toast owns its countdown and siblings mounting or unmounting must not
  // restart it.
  useEffect(() => {
    if (!duration || duration <= 0) return;
    const timer = setTimeout(handleClose, duration);
    return () => clearTimeout(timer);
  }, [duration, handleClose]);

  const shouldAnimateProgress = progress && Boolean(duration && duration > 0);

  useEffect(() => {
    if (!shouldAnimateProgress) {
      setProgressComplete(false);
      return;
    }

    setProgressComplete(false);
    const frame = requestAnimationFrame(() => setProgressComplete(true));

    return () => cancelAnimationFrame(frame);
  }, [duration, shouldAnimateProgress]);

  const iconColor = TOAST_COLORS[type];
  const shouldShowIcon = icon !== false;
  const displayIcon =
    icon === true || icon === undefined
      ? getDefaultIcon(type, config.iconSize, iconColor)
      : icon;
  const renderedAction =
    isValidElement<ActionProps>(action) && typeof action.type !== "string"
      ? cloneElement(action, {
          size: "xs",
          variant: "inverse",
          background: false,
          themeMode: "dark",
          themeProductContext,
          children: (
            <span style={{ color: colorScheme.actionText }}>
              {action.props.children}
            </span>
          ),
        })
      : action;
  const hasAction = Boolean(action);
  const hasCloseButton = showCloseButton && Boolean(onClose);
  const hasActions = hasAction || hasCloseButton;
  const role = type === "neutral" || type === "success" ? "status" : "alert";
  const ariaLive =
    type === "neutral" || type === "success" ? "polite" : "assertive";

  return (
    <Box
      testID={testID}
      backgroundColor={colorScheme.background}
      borderRadius={theme.shape.toast.borderRadius}
      minHeight={config.minHeight}
      flexDirection="row"
      alignItems="center"
      justifyContent="center"
      overflow="hidden"
      width={config.maxWidth}
      maxWidth="100%"
      role={role}
      aria-live={ariaLive}
      data-toast-id={id}
      style={{
        opacity: visible && !dismissing ? 1 : 0,
        transform:
          visible && !dismissing ? "translateY(0)" : "translateY(-8px)",
        transition: `opacity ${ANIMATION_DURATION}ms ease-out, transform ${ANIMATION_DURATION}ms ease-out`,
        boxShadow: colorScheme.shadow,
        backdropFilter: colorScheme.backdropFilter,
      }}
    >
      <Box
        flex={1}
        minHeight={config.minHeight}
        minWidth={0}
        flexDirection="row"
        alignItems="center"
        gap={config.gap}
        paddingHorizontal={config.paddingHorizontal}
        paddingVertical={config.paddingVertical}
      >
        {shouldShowIcon && (
          <Box
            width={config.iconSize}
            height={config.iconSize}
            alignItems="center"
            justifyContent="center"
            flexShrink={0}
          >
            {icon === true || icon === undefined ? (
              displayIcon
            ) : (
              <Icon size={config.iconSize} color={iconColor}>
                {displayIcon}
              </Icon>
            )}
          </Box>
        )}

        <Box flex={1} minWidth={0} gap={2}>
          {title && (
            <Text
              color={colorScheme.text}
              fontSize={config.fontSize}
              lineHeight={config.lineHeight}
              fontWeight="500"
              numberOfLines={1}
            >
              {title}
            </Text>
          )}
          {description && (
            <Text
              testID="toast-description"
              color={colorScheme.descriptionText}
              fontSize={14}
              lineHeight={18}
              numberOfLines={descriptionLines}
            >
              {description}
            </Text>
          )}
        </Box>

        {hasActions && (
          <Box
            height={32}
            flexDirection="row"
            alignItems="center"
            gap={config.gap}
            flexShrink={0}
          >
            {renderedAction}

            {hasAction && hasCloseButton && (
              <Box
                width={1}
                height="100%"
                backgroundColor={colorScheme.divider}
                testID="toast-divider"
              />
            )}

            {hasCloseButton && (
              <Box
                onPress={handleClose}
                width={config.closeButtonSize}
                height={config.closeButtonSize}
                alignItems="center"
                justifyContent="center"
                flexShrink={0}
                role="button"
                aria-label="Dismiss notification"
              >
                <Remove
                  variant="line"
                  size={config.closeIconSize}
                  color={colorScheme.text}
                />
              </Box>
            )}
          </Box>
        )}
      </Box>

      {progress && (
        <Box
          testID="toast-progress"
          position="absolute"
          left={0}
          right={0}
          bottom={0}
          height={PROGRESS_HEIGHT}
          overflow="hidden"
        >
          <Box
            testID="toast-progress-bar"
            width="100%"
            height="100%"
            backgroundColor={iconColor}
            style={{
              transform: `scaleX(${progressComplete ? 0 : 1})`,
              transformOrigin: "left center",
              transition: shouldAnimateProgress
                ? `transform ${duration}ms linear`
                : undefined,
            }}
          />
        </Box>
      )}
    </Box>
  );
};

Toast.displayName = "Toast";
